var _react_refresh_temp_1;
var _react_refresh_temp_2;
_react_refresh_temp_2 = $RefreshSig$();
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState, } from 'react';
import { webCrypto } from '@lucidlink/ts-crypto/web-crypto';
import { isNil, raceWithAbort } from '@lucidlink/tscore';
import { translate, useModal, useToastService } from '@lucidlink/uicore';
import { usePlugFilespaceClient, useReporting, } from '@lucidlink/workspacemanagement';
import { calculatePercentDone, generateScopeString, getPingManager, LEGACY_MAX_MISSED_PINGS, sendChunkThroughSwInternalChannel, sendWithStallCheck, streamFileChunksConcurrently, streamFileChunksSequentially, } from '../file-contexts-common';
import { DownloadStartFailedModal } from './components';
import { BATCH_ENTRY_QUEUE_CAPACITY, CONCURRENT_DOWNLOAD_FILE_SIZE_THRESHOLD, DEFAULT_FILE_DOWNLOAD_CHUNK_SIZE, MainToSwBroadcastMessageTypes, SwToMainBroadcastMessageTypes, ZERO_BYTES_BIG_INT, } from './constants';
import { DownloadDataContext, DownloadMethodsContext, } from './DownloadContexts';
import { useConfirmUnreliableDownload } from './hooks';
import { AsyncQueue, bytesToMb, createArchiveStream, entryDownload, generateDownloadUrl, getFolderDepth, getRecognizedFileExtensionFromFileName, initiateDownloadViaIframe, postMessageToSwOnBroadcastChannel, secondsSince, startSWAndWaitOnActivate, toErrorMessage, walkDownloadableFiles, } from './services';
const DOWNLOAD_PERCENT_DONE_UPDATE_RATE = 5;
const feedDownloadableEntries = async ({ entries, entryQueue, readDir, signal, }) => {
    const discovered = walkDownloadableFiles(entries, { readDir, signal });
    for await (const entry of discovered) {
        await entryQueue.push(entry);
    }
};
async function* prependFirstEntry(firstEntry, remainingEntries) {
    yield firstEntry;
    yield* { [Symbol.asyncIterator]: () => remainingEntries };
}
export const DownloadProvider = ({ children }) => {
    _react_refresh_temp_2();
    const { closeModal: closeDownloadStartFailedModal, openModal: openDownloadStartFailedModal, } = useModal({ closeOnUnmount: true });
    const { confirmUnreliableDownload, shouldConfirmDownload } = useConfirmUnreliableDownload();
    const { plugFilespaceClient } = usePlugFilespaceClient();
    const { addToast, removeToast } = useToastService();
    // Mock state, at this moment I don't know what I'll need here
    const [activeDownloads, setActiveDownloads] = useState([]);
    const [activeBatchDownloads, setActiveBatchDownloads] = useState([]);
    const addActiveBatchDownload = useCallback((batchDownload) => {
        setActiveBatchDownloads((prev) => [...prev, batchDownload]);
    }, []);
    const removeActiveBatchDownload = useCallback(({ folderId, fsId }) => {
        setActiveBatchDownloads((prev) => {
            const index = prev.findIndex((batchDownload) => batchDownload.folderId === folderId &&
                batchDownload.fsId === fsId);
            if (index === -1) {
                return prev;
            }
            return [...prev.slice(0, index), ...prev.slice(index + 1)];
        });
    }, []);
    const addActiveDownload = useCallback((activeDownload) => {
        setActiveDownloads((prev) => {
            const alreadyExists = prev.find((prevActiveDownload) => prevActiveDownload.scope === activeDownload.scope);
            if (alreadyExists) {
                return prev;
            }
            return [...prev, activeDownload];
        });
    }, []);
    const updateActiveDownloadPercentDone = useCallback((scope, currentOffset) => {
        setActiveDownloads((prev) => {
            const currentActiveDownload = prev.find((prevActiveDownload) => prevActiveDownload.scope === scope);
            if (!currentActiveDownload) {
                return prev;
            }
            const { percentDone: currentPercentDone, size } = currentActiveDownload;
            if (isNil(size)) {
                return prev;
            }
            const newPercentDone = calculatePercentDone(currentOffset, size);
            // Basic throttling, as downloading multiple files at the same time will cause
            // huge rerender requests and anyways we do not really use this data right now
            const shouldUpdatePercentage = newPercentDone > currentPercentDone &&
                newPercentDone % DOWNLOAD_PERCENT_DONE_UPDATE_RATE === 0;
            if (!shouldUpdatePercentage) {
                return prev;
            }
            const previousActiveDownloadsFiltered = prev.filter((prevActiveDownload) => prevActiveDownload.scope !== scope);
            return [
                ...previousActiveDownloadsFiltered,
                { ...currentActiveDownload, percentDone: newPercentDone },
            ];
        });
    }, []);
    const removeActiveDownload = useCallback((scope) => {
        setActiveDownloads((prev) => {
            const filteredPrev = prev.filter((prevActiveDownload) => prevActiveDownload.scope !== scope);
            return [...filteredPrev];
        });
    }, []);
    const activeDownloadsLength = activeDownloads.length;
    // Unfortunately we cannot do anything else. A prompt will be shown, but we cannot
    // fire any action right before the real unload. We cannot get information if user
    // agreed or cancelled closing the page, so download will just stall.
    useEffect(() => {
        const beforeUnloadHandler = (event) => {
            event.preventDefault(); // Required in some browsers
        };
        if (activeDownloadsLength) {
            window.addEventListener('beforeunload', beforeUnloadHandler);
        }
        return () => {
            window.removeEventListener('beforeunload', beforeUnloadHandler);
        };
    }, [activeDownloadsLength]);
    const { reportEvent } = useReporting();
    const showDownloadStartFailedModal = useCallback((shouldSuggestCorsSettings) => openDownloadStartFailedModal({
        content: (_jsx(DownloadStartFailedModal, { closeModal: closeDownloadStartFailedModal, shouldShowAdditionalDescription: Boolean(shouldSuggestCorsSettings) })),
    }), [closeDownloadStartFailedModal, openDownloadStartFailedModal]);
    const startDownload = useCallback(async ({ downloadChunk: downloadChunkOverride, fsEntryKey, fsEntryName, fsEntrySize, fsId, isCustomStorage, onDownloadComplete, onDownloadFail, onDownloadStart, onDownloadStartFail, workspaceId, }) => {
        const downloadStrategy = fsEntrySize > CONCURRENT_DOWNLOAD_FILE_SIZE_THRESHOLD
            ? 'concurrent'
            : 'sequential';
        const fileExtension = getRecognizedFileExtensionFromFileName(fsEntryName);
        const { promise: activationPromise, resolve: activationResolver } = Promise.withResolvers();
        const scope = webCrypto.randomUUID();
        const scopeString = generateScopeString(scope);
        const swBroadcastChannel = new BroadcastChannel(scope);
        const downloadUrl = generateDownloadUrl({
            fsEntryId: fsEntryKey.id,
            fsEntryName,
            fsEntrySize,
            scope,
        });
        // TODO: Think how to signal operation without throwing an error
        // errors thrown in the interval cannot be caught anywhere
        const pingManager = getPingManager({
            maxMissedPings: LEGACY_MAX_MISSED_PINGS,
            pingFn: (id) => {
                postMessageToSwOnBroadcastChannel(swBroadcastChannel, [
                    MainToSwBroadcastMessageTypes.PING,
                    { id },
                ]);
            },
        });
        let isCancelledByBrowser = false;
        const abortController = new AbortController();
        swBroadcastChannel.onmessage = (event) => {
            const [type, payload] = event.data;
            switch (type) {
                case SwToMainBroadcastMessageTypes.ACTIVATION:
                    {
                        activationResolver();
                    }
                    break;
                case SwToMainBroadcastMessageTypes.PONG: {
                    const { id } = payload;
                    pingManager.acknowledgePing(id);
                    break;
                }
                case SwToMainBroadcastMessageTypes.STREAM_CANCELLED:
                    isCancelledByBrowser = true;
                    abortController.abort();
                    break;
                default:
                    break;
            }
        };
        let serviceWorkerRegistration;
        try {
            serviceWorkerRegistration = await startSWAndWaitOnActivate({
                scope: scopeString,
                activationPromise: activationPromise,
            });
        }
        catch (error) {
            // eslint-disable-next-line no-console -- Let's see how to handle, we don't have custom errors yet
            console.error('Error while setting up worker:', error);
            swBroadcastChannel.close();
            const errorMessage = toErrorMessage(error);
            onDownloadStartFail({
                isCustomFilespace: Boolean(isCustomStorage),
                errorMessage,
                downloadStrategy,
                fsId,
                workspaceId,
            });
            showDownloadStartFailedModal(isCustomStorage);
            return;
        }
        // Update the react state now, so users will be warned if they try to close the tab
        addActiveDownload({
            scope,
            id: fsEntryKey.id,
            name: fsEntryName,
            size: fsEntrySize,
            percentDone: 0,
        });
        onDownloadStart({
            fileSize: bytesToMb(fsEntrySize),
            fsId,
            workspaceId,
            ...(fileExtension && { fileExtension }),
        });
        // Toasts needs additional unique identifier - scope is used here - as you can download same fsEntry
        // multiple times, causing the Toast Component to render components with same key
        const successfulDownloadStartToastId = `successful-download-start-${fsEntryKey.id}-${scope}`;
        const successfulDownloadFinishToastId = `successful-download-finish-${fsEntryKey.id}-${scope}`;
        const failedDownloadToastId = `failed-download-${fsEntryKey.id}-${scope}`;
        addToast({
            id: successfulDownloadStartToastId,
            'data-testid': 'successful-download-start',
            kind: 'informative',
            content: {
                message: translate('Make sure to keep your tab open until download is complete.'),
                label: translate('Download in progress'),
            },
            hasCloseButton: true,
        });
        // Starting the ping as soon as we have a service worker instance
        pingManager.start();
        // We could also have a broadcast message here from the SW
        // that it intercepted the download URL if we want to be sure
        // before starting the downloading function
        const cleanUpIframe = initiateDownloadViaIframe(downloadUrl);
        const willDownloadConcurrently = downloadStrategy === 'concurrent';
        // eslint-disable-next-line no-console -- shh
        console.log(`Start of download with ${downloadStrategy} strategy`);
        let currentOffset = ZERO_BYTES_BIG_INT;
        const downloadChunk = downloadChunkOverride ??
            ((buffer, offset) => {
                abortController.signal.throwIfAborted();
                return raceWithAbort(plugFilespaceClient({
                    action: (fsClient) => fsClient
                        .fileSystem()
                        .readFile(fsEntryKey, buffer, offset),
                    filespaceId: fsId,
                    workspaceId,
                }), abortController.signal);
            });
        const { sendChunk, sendEnd } = sendChunkThroughSwInternalChannel(serviceWorkerRegistration);
        // Actual download starts here, everything before that is just preparing the
        // SW and initiating the download. Starting to measure here will lead to better
        // speed / time taken accuracy
        const startDownloadTime = Date.now();
        const chunks = willDownloadConcurrently
            ? streamFileChunksConcurrently({
                chunkSize: DEFAULT_FILE_DOWNLOAD_CHUNK_SIZE,
                downloadChunk,
                fileSize: fsEntrySize,
            })
            : streamFileChunksSequentially({
                chunkSize: DEFAULT_FILE_DOWNLOAD_CHUNK_SIZE,
                downloadChunk,
            });
        try {
            let receivedBytes = ZERO_BYTES_BIG_INT;
            for await (const chunk of chunks) {
                receivedBytes += BigInt(chunk.byteLength);
                await sendWithStallCheck(() => sendChunk(chunk), {
                    isRecipientAlive: pingManager.isPeerResponsive,
                    signal: abortController.signal,
                });
                currentOffset = receivedBytes;
                updateActiveDownloadPercentDone(scope, receivedBytes);
            }
            await sendWithStallCheck(sendEnd, {
                isRecipientAlive: pingManager.isPeerResponsive,
                signal: abortController.signal,
            });
            removeToast(successfulDownloadStartToastId);
            addToast({
                id: successfulDownloadFinishToastId,
                'data-testid': 'successful-download-finish',
                kind: 'positive',
                content: {
                    message: translate('File download completed.'),
                },
                hasCloseButton: true,
            });
            onDownloadComplete({
                downloadTime: secondsSince(startDownloadTime),
                fileSize: bytesToMb(fsEntrySize),
                fsId,
                ...(fileExtension && { fileExtension }),
                workspaceId,
            });
        }
        catch (error) {
            // Handle errors here, we should have access to useToast
            // or other methods to handle it. Up to discussion, if we want
            // we can just re-throw the error. For now it's nice because
            // we can have a clean up here.
            const errorMessage = toErrorMessage(error);
            removeToast(successfulDownloadStartToastId);
            if (isCancelledByBrowser) {
                // The user threw the download away on purpose. The Service
                // Worker is already tearing itself down, so there is
                // nothing to report and nothing to tell them.
                return;
            }
            else if (currentOffset === 0n) {
                onDownloadStartFail({
                    isCustomFilespace: Boolean(isCustomStorage),
                    errorMessage,
                    downloadStrategy,
                    fsId,
                    workspaceId,
                });
                showDownloadStartFailedModal(isCustomStorage);
            }
            else {
                onDownloadFail({
                    downloadTime: secondsSince(startDownloadTime),
                    fileSize: bytesToMb(fsEntrySize),
                    downloadedSize: bytesToMb(currentOffset),
                    errorMessage,
                    downloadStrategy,
                    failedChunk: Number(currentOffset /
                        BigInt(DEFAULT_FILE_DOWNLOAD_CHUNK_SIZE)),
                    fsId,
                    workspaceId,
                });
                addToast({
                    id: failedDownloadToastId,
                    'data-testid': 'failed-download',
                    kind: 'negative',
                    dismissTempo: 'never',
                    content: {
                        message: translate('The file was not fully downloaded. Please try again.'),
                        label: translate('Download failed'),
                    },
                    hasCloseButton: true,
                });
            }
            if (!isCancelledByBrowser) {
                postMessageToSwOnBroadcastChannel(swBroadcastChannel, [
                    MainToSwBroadcastMessageTypes.ERROR,
                ]);
                // eslint-disable-next-line no-console -- Let's see how to handle
                console.error('Error in main thread:', error);
            }
        }
        finally {
            pingManager.stop();
            swBroadcastChannel.close();
            removeActiveDownload(scope);
            cleanUpIframe();
        }
    }, [
        addActiveDownload,
        addToast,
        plugFilespaceClient,
        removeActiveDownload,
        removeToast,
        showDownloadStartFailedModal,
        updateActiveDownloadPercentDone,
    ]);
    const startBatchDownload = useCallback(async ({ archiveName, entries, folderId, fsId, isCustomStorage, plugFilespaceClientOverride, workspaceId, }) => {
        if (entries.length === 0) {
            return;
        }
        const batchDownload = { folderId, fsId };
        addActiveBatchDownload(batchDownload);
        const plugConfiguredFilespaceClient = plugFilespaceClientOverride ??
            ((action) => plugFilespaceClient({
                action,
                filespaceId: fsId,
                workspaceId,
            }));
        const abortController = new AbortController();
        const entryQueue = new AsyncQueue({
            capacity: BATCH_ENTRY_QUEUE_CAPACITY,
        });
        const runDiscovery = async () => {
            try {
                await plugConfiguredFilespaceClient((fsClient) => feedDownloadableEntries({
                    entries,
                    entryQueue,
                    readDir: (entryKey) => fsClient.fileSystem().readDir(entryKey),
                    signal: abortController.signal,
                }));
                entryQueue.close();
            }
            catch (error) {
                entryQueue.fail(error);
            }
        };
        // Producer. Deliberately not awaited here: it runs alongside the
        // archive stream and normally finishes long before it. Never
        // rejects - a failed walk is handed to the consumer instead.
        const walkPromise = runDiscovery();
        const stopDiscovery = async () => {
            // The walk may still be blocked in `push()` - closing the queue
            // wakes it, and the abort stops it from reading the rest of the
            // tree.
            abortController.abort();
            entryQueue.close();
            await walkPromise;
        };
        const discoveredEntries = entryQueue[Symbol.asyncIterator]();
        let firstDiscoveredEntry;
        let discoveryError;
        try {
            firstDiscoveredEntry = await discoveredEntries.next();
        }
        catch (error) {
            firstDiscoveredEntry = null;
            discoveryError = error;
        }
        // The walk failed, or found nothing to archive. Either way there
        // is no archive to stream, and no service worker was registered.
        if (!firstDiscoveredEntry || firstDiscoveredEntry.done) {
            await stopDiscovery();
            // Nothing to archive is not a failure - only a broken walk is.
            if (discoveryError) {
                reportEvent({
                    name: 'Folder Download Start Failed',
                    params: {
                        Error: toErrorMessage(discoveryError),
                        'Failure Stage': 'discovery',
                        'Is Custom Filespace': Boolean(isCustomStorage),
                    },
                    'Filespace ID': fsId,
                    'Workspace ID': workspaceId,
                });
                showDownloadStartFailedModal(isCustomStorage);
            }
            removeActiveBatchDownload(batchDownload);
            return;
        }
        const firstEntry = firstDiscoveredEntry.value;
        const { promise: activationPromise, resolve: activationResolver } = Promise.withResolvers();
        const scope = webCrypto.randomUUID();
        const scopeString = generateScopeString(scope);
        const swBroadcastChannel = new BroadcastChannel(scope);
        const archiveFsEntryId = `batch-${scope}`;
        const downloadUrl = generateDownloadUrl({
            fsEntryId: archiveFsEntryId,
            fsEntryName: archiveName,
            scope,
        });
        const pingManager = getPingManager({
            maxMissedPings: LEGACY_MAX_MISSED_PINGS,
            pingFn: (id) => {
                postMessageToSwOnBroadcastChannel(swBroadcastChannel, [
                    MainToSwBroadcastMessageTypes.PING,
                    { id },
                ]);
            },
        });
        let isCancelledByBrowser = false;
        swBroadcastChannel.onmessage = (event) => {
            const [type, payload] = event.data;
            switch (type) {
                case SwToMainBroadcastMessageTypes.ACTIVATION:
                    activationResolver();
                    break;
                case SwToMainBroadcastMessageTypes.PONG: {
                    pingManager.acknowledgePing(payload.id);
                    break;
                }
                case SwToMainBroadcastMessageTypes.STREAM_CANCELLED:
                    isCancelledByBrowser = true;
                    abortController.abort();
                    break;
                default:
                    break;
            }
        };
        let serviceWorkerRegistration;
        try {
            serviceWorkerRegistration = await startSWAndWaitOnActivate({
                scope: scopeString,
                activationPromise,
            });
        }
        catch (error) {
            swBroadcastChannel.close();
            await stopDiscovery();
            reportEvent({
                name: 'Folder Download Start Failed',
                params: {
                    Error: toErrorMessage(error),
                    'Failure Stage': 'service worker registration',
                    'Is Custom Filespace': Boolean(isCustomStorage),
                },
                'Filespace ID': fsId,
                'Workspace ID': workspaceId,
            });
            showDownloadStartFailedModal(isCustomStorage);
            removeActiveBatchDownload(batchDownload);
            return;
        }
        addActiveDownload({
            scope,
            id: archiveFsEntryId,
            name: archiveName,
            percentDone: 0,
        });
        const successfulDownloadStartToastId = `successful-download-start-${archiveFsEntryId}-${scope}`;
        const successfulDownloadFinishToastId = `successful-download-finish-${archiveFsEntryId}-${scope}`;
        const failedDownloadToastId = `failed-download-${archiveFsEntryId}-${scope}`;
        addToast({
            id: successfulDownloadStartToastId,
            'data-testid': 'successful-download-start',
            kind: 'informative',
            content: {
                message: translate('Make sure to keep your tab open until download is complete.'),
                label: translate('Download in progress'),
            },
            hasCloseButton: true,
        });
        pingManager.start();
        const cleanUpIframe = initiateDownloadViaIframe(downloadUrl);
        const { sendChunk, sendEnd } = sendChunkThroughSwInternalChannel(serviceWorkerRegistration);
        let hasReadFileContent = false;
        const createChunkDownloader = (file) => async (buffer, offset) => {
            abortController.signal.throwIfAborted();
            const bytesRead = await raceWithAbort(plugConfiguredFilespaceClient((fsClient) => fsClient
                .fileSystem()
                .readFile(file.fsEntryKey, buffer, offset)), abortController.signal);
            hasReadFileContent = true;
            return bytesRead;
        };
        const startDownloadTime = Date.now();
        let archivedFileCount = 0;
        let archivedBytes = ZERO_BYTES_BIG_INT;
        let deepestFolderDepth = 0;
        const reader = createArchiveStream({
            createChunkDownloader,
            downloadableEntries: prependFirstEntry(firstEntry, discoveredEntries),
            onFileArchived: (file) => {
                archivedFileCount += 1;
                archivedBytes += file.fsEntrySize;
                deepestFolderDepth = Math.max(deepestFolderDepth, getFolderDepth(file.zipPath));
            },
        }).getReader();
        try {
            let result = await reader.read();
            while (!result.done) {
                // The walk can already be finished by the time the user
                // hits Cancel, so the abort has to be observed here too.
                abortController.signal.throwIfAborted();
                const chunk = result.value;
                await sendWithStallCheck(() => sendChunk(chunk), {
                    isRecipientAlive: pingManager.isPeerResponsive,
                    signal: abortController.signal,
                });
                result = await reader.read();
            }
            await sendWithStallCheck(sendEnd, {
                isRecipientAlive: pingManager.isPeerResponsive,
                signal: abortController.signal,
            });
            removeToast(successfulDownloadStartToastId);
            addToast({
                id: successfulDownloadFinishToastId,
                'data-testid': 'successful-download-finish',
                kind: 'positive',
                content: {
                    message: translate('Download completed.'),
                },
                hasCloseButton: true,
            });
            reportEvent({
                name: 'Folder Download Completed',
                params: {
                    'Download Time': secondsSince(startDownloadTime),
                    'Files Count': archivedFileCount,
                    'Folder Depth': deepestFolderDepth,
                    'Total Size': bytesToMb(archivedBytes),
                },
                'Filespace ID': fsId,
                'Workspace ID': workspaceId,
            });
        }
        catch (error) {
            removeToast(successfulDownloadStartToastId);
            if (isCancelledByBrowser) {
                // The user threw the download away on purpose. The Service
                // Worker is already tearing itself down, so there is
                // nothing to report and nothing to tell them.
                return;
            }
            else if (hasReadFileContent) {
                reportEvent({
                    name: 'Folder Download Failed',
                    params: {
                        'Download Time': secondsSince(startDownloadTime),
                        'Downloaded Files Count': archivedFileCount,
                        'Downloaded Size': bytesToMb(archivedBytes),
                        Error: toErrorMessage(error),
                    },
                    'Filespace ID': fsId,
                    'Workspace ID': workspaceId,
                });
                addToast({
                    id: failedDownloadToastId,
                    'data-testid': 'failed-download',
                    kind: 'negative',
                    dismissTempo: 'never',
                    content: {
                        message: translate('The downloaded archive is incomplete. Please try again.'),
                        label: translate('Download failed'),
                    },
                    hasCloseButton: true,
                });
            }
            else {
                reportEvent({
                    name: 'Folder Download Start Failed',
                    params: {
                        Error: toErrorMessage(error),
                        'Failure Stage': 'download start',
                        'Is Custom Filespace': Boolean(isCustomStorage),
                    },
                    'Filespace ID': fsId,
                    'Workspace ID': workspaceId,
                });
                showDownloadStartFailedModal(isCustomStorage);
            }
            if (!isCancelledByBrowser) {
                postMessageToSwOnBroadcastChannel(swBroadcastChannel, [
                    MainToSwBroadcastMessageTypes.ERROR,
                ]);
            }
        }
        finally {
            try {
                await reader.cancel();
            }
            catch {
            }
            await stopDiscovery();
            pingManager.stop();
            swBroadcastChannel.close();
            removeActiveDownload(scope);
            cleanUpIframe();
            removeActiveBatchDownload(batchDownload);
        }
    }, [
        addActiveBatchDownload,
        addActiveDownload,
        addToast,
        plugFilespaceClient,
        removeActiveBatchDownload,
        removeActiveDownload,
        removeToast,
        reportEvent,
        showDownloadStartFailedModal,
    ]);
    const entryDownloadEnv = useMemo(() => ({
        addActiveBatchDownload,
        addActiveDownload,
        addToast,
        removeActiveBatchDownload,
        removeActiveDownload,
        removeToast,
        showDownloadStartFailedModal,
        updateActiveDownloadPercentDone,
    }), [
        addActiveBatchDownload,
        addActiveDownload,
        addToast,
        removeActiveBatchDownload,
        removeActiveDownload,
        removeToast,
        showDownloadStartFailedModal,
        updateActiveDownloadPercentDone,
    ]);
    const startEntryDownload = useCallback(async (args) => {
        if (shouldConfirmDownload && !(await confirmUnreliableDownload())) {
            return;
        }
        await entryDownload(args, entryDownloadEnv);
    }, [confirmUnreliableDownload, entryDownloadEnv, shouldConfirmDownload]);
    const downloadDataValue = useMemo(() => {
        return {
            activeBatchDownloads,
            activeDownloads,
        };
    }, [activeBatchDownloads, activeDownloads]);
    const downloadMethodsValue = useMemo(() => {
        return {
            startBatchDownload,
            startDownload,
            startEntryDownload,
        };
    }, [startBatchDownload, startDownload, startEntryDownload]);
    return (_jsx(DownloadDataContext.Provider, { value: downloadDataValue, children: _jsx(DownloadMethodsContext.Provider, { value: downloadMethodsValue, children: children }) }));
};
_react_refresh_temp_1 = DownloadProvider;
$RefreshReg$(_react_refresh_temp_1, "DownloadProvider");
_react_refresh_temp_2(DownloadProvider, "bb4JIODO5NaZ0MT9phkmmRmz6aU=", false, () => [useModal, useConfirmUnreliableDownload, usePlugFilespaceClient, useToastService, useReporting]);
